import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type * as extra from '../../extra'
import type * as admin from '../admin'
import type * as agent from '../agent'
import type * as common from '../common'
import type * as dms from '../dms'
import type * as integrations from '../integrations'
import type * as mosaic from '../mosaic'

import type { Package } from './package'

/**
 * Represents something to be deployed in our site
 */
export type Resource<TType extends Resource.Type = Resource.Type> =
  TType extends any
    ? {
        /**
         * Used to indicate that this {@link Resource} is an evolution of another
         * {@link Resource} from the {@link Package.precursor}
         *
         * Usually, this is automatically determined based on the names of the
         * {@link Resource}, but in case the name has changed, this can be used to
         * explicitly link this {@link Resource} to its precursor
         *
         * Helps determine where updates should be made instead of creating something
         * new
         */
        precursor?: Resource
        type: TType
        payload: Resource.Payload<TType>
      }
    : never
export namespace Resource {
  /**
   * {@link Resource} with its {@link extra.quiver.resources.Id}
   */
  export type WithId = Resource & common.Identified<extra.quiver.resources.Id>

  /**
   * TODO: explain what this enum is, when it is used and why, as well as any examples
   *
   * TODO: add JSDoc comments to each enum value to explain it
   */
  export enum Type {
    App = 'app',
    AppPage = 'app-page',
    Scenario = 'scenario',
    MosaicPage = 'mosaic-page',
    MosaicWidget = 'mosaic-widget',
    MosaicAction = 'mosaic-action',
    DmsDimension = 'dms-dimension',
    DmsEntity = 'dms-entity',
    DmsSnapshot = 'dms-snapshot',
    DmsAttribute = 'dms-attribute',
    /**
     * TODO: used to add a constraint to a new or existing attribute
     */
    DmsAttributeConstraint = 'dms-attribute-constraint',
    DmsPurpose = 'dms-purpose',
    DmsEnum = 'dms-enum',
    DmsEnumEntry = 'dms-enum-entry',
    // TODO: maybe replace with the JSON schema version of structures
    DmsStructure = 'dms-structure',
    DmsRecord = 'dms-record',
    DmsUpload = 'dms-upload',
    Agent = 'agent',
    IntegrationConnectorDefinition = 'integration-connector-definition',
  }
  // Needs to be before namespace is defined
  const TYPES: ReadonlySet<Type> = new Set(Object.values(Type) as Type[])
  export namespace Type {
    export const ALL: readonly Type[] = Array.from(TYPES)
    export const isType = TypeChecker.Factory.oneOf(TYPES)
    export const assertIsType: TypeAsserter<Type> =
      TypeAsserter.Factory.from(isType)
  }

  /**
   * TODO:
   */
  export type Payload<TType extends Type> = {
    [Type.App]: admin.App.Input
    [Type.AppPage]: admin.App.Page.Input & {
      /**
       * Because {@link admin.App.Id} is randomly generated, we cannot know it
       * ahead of time. Instead, we must use {@link admin.App.alias}
       */
      appAlias: admin.App['alias']
    }
    [Type.Scenario]: unknown
    [Type.MosaicPage]: mosaic.Page.Input
    [Type.MosaicWidget]: mosaic.Widget.Input
    [Type.MosaicAction]: mosaic.Action.Input
    [Type.DmsDimension]: Required.Semi<
      Omit<dms.models.Dimension.Input, 'entities'>,
      'partialId'
    >
    [Type.DmsEntity]: Required.Semi<
      Omit<dms.models.Entity.Input, 'additionalAttributes'>,
      'partialId'
    >
    [Type.DmsSnapshot]: Required.Semi<
      Omit<dms.models.Snapshot.Input, 'attributes'>,
      'partialId'
    >
    [Type.DmsAttribute]: Required.Semi<dms.models.Attribute.Input, 'partialId'>
    [Type.DmsAttributeConstraint]: common.value.Constraints & {
      attributeId: dms.models.Attribute.Id
    }
    [Type.DmsPurpose]: Required.Semi<dms.models.Purpose.Input, 'partialId'>
    [Type.DmsEnum]: Required.Semi<
      Omit<dms.models.Enum.Input, 'entries'>,
      'partialId'
    >
    [Type.DmsEnumEntry]: Required.Semi<dms.models.Enum.Entry.Input, 'id'> & {
      enumId: dms.models.Enum.Id
    }
    [Type.DmsStructure]: Required.Semi<dms.models.Structure.Input, 'partialId'>
    [Type.DmsRecord]: dms.data.Record.Input & {
      modelId: dms.models.ForRecord.Id
    }
    [Type.DmsUpload]: unknown
    [Type.Agent]: {
      agentId: agent.Agent.Id
      pageContext?: agent.ZoneConfig.PageContext
    }
    [Type.IntegrationConnectorDefinition]: integrations.Connector.Definition.Input
  }[TType]
}
